import { useState } from "react";
import {
  Box,
  Button,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Select,
  type SelectChangeEvent,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { GROUP_BY_OPTIONS, type FilterEntry, type GroupBy } from "../types";

interface FilterChipsProps {
  filters: FilterEntry[];
  onChange: (next: FilterEntry[]) => void;
}

export function FilterChips({ filters, onChange }: FilterChipsProps) {
  const [adding, setAdding] = useState(false);
  const [draftColumn, setDraftColumn] = useState<GroupBy>("case_id");
  const [draftValue, setDraftValue] = useState("");

  const commitDraft = () => {
    if (!draftValue.trim()) return;
    onChange([
      ...filters.filter((f) => f.column !== draftColumn),
      { column: draftColumn, value: draftValue.trim() },
    ]);
    setDraftValue("");
    setAdding(false);
  };

  const removeFilter = (column: GroupBy) => {
    onChange(filters.filter((f) => f.column !== column));
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      {filters.map((f) => (
        <Chip
          key={f.column}
          label={`${labelFor(f.column)}: ${f.value}`}
          onDelete={() => removeFilter(f.column)}
        />
      ))}
      {adding ? (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Select
            value={draftColumn}
            size="small"
            onChange={(e: SelectChangeEvent) => setDraftColumn(e.target.value as GroupBy)}
          >
            {GROUP_BY_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
          <TextField
            size="small"
            placeholder="Value"
            value={draftValue}
            onChange={(e) => setDraftValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitDraft();
              if (e.key === "Escape") setAdding(false);
            }}
            autoFocus
          />
          <Button size="small" onClick={commitDraft}>
            Add
          </Button>
          <Button size="small" onClick={() => setAdding(false)} color="inherit">
            Cancel
          </Button>
        </Box>
      ) : (
        <Button size="small" startIcon={<AddIcon />} onClick={() => setAdding(true)}>
          Add filter
        </Button>
      )}
    </Stack>
  );
}

function labelFor(col: GroupBy): string {
  return GROUP_BY_OPTIONS.find((o) => o.value === col)?.label ?? col;
}
